import { PayloadAction } from '@reduxjs/toolkit';
import { CustomerInfo } from '@revenuecat/purchases-capacitor';
import dayjs, { Dayjs } from 'dayjs';
import posthog from 'posthog-js';
import { CurrentUserDTO } from 'services/api/models/UserDTO';
import resetAll from 'store/shared/resetAll';
import createSlice from 'utils/@reduxjs/toolkit';
import { UserState } from './types';

export const initialState: UserState = {
  currentUser: undefined,
  currentUserSubscription: undefined,
};

// Utility function to check if user has a valid non-renewable subscription
export const validNonRenewableSubscriptions = (
  allPurchaseDates: Record<string, string | null> | undefined,
): Dayjs[] => {
  if (!allPurchaseDates) return [];

  const oneWeekAgo = dayjs().subtract(1, 'week');

  return Object.entries(allPurchaseDates || {})
    .filter(([key]) => key.endsWith('_1w'))
    .flatMap(([, dateString]) => {
      if (!dateString) return [];
      if (dayjs(dateString).isAfter(oneWeekAgo)) return dayjs(dateString);
      return [];
    })
    .sort((a, b) => a.diff(b));
};

const slice = createSlice({
  name: 'users',
  initialState,
  extraReducers: (builder: any) => builder.addCase(resetAll, () => initialState),
  reducers: {
    setCurrentUser: (state, action: PayloadAction<CurrentUserDTO>) => {
      state.currentUser = action.payload;
    },
    setEmailConsents: (state, action: PayloadAction<boolean>) => {
      if (state.currentUser)
        state.currentUser = { ...state.currentUser, consents_to_email_notifications: action.payload };
    },
    disableNPSSurvey: state => {
      if (state.currentUser) state.currentUser = { ...state.currentUser, should_ask_for_nps: false };
    },
    setCurrentUserSubscription: (state, action: PayloadAction<CustomerInfo>) => {
      state.currentUserSubscription = action.payload;
      const hasValidNonRenewableSubscription =
        validNonRenewableSubscriptions(action.payload.allPurchaseDates).length > 0;
      const activeSubscription =
        action.payload.activeSubscriptions.length > 0
          ? action.payload.activeSubscriptions[0]
          : hasValidNonRenewableSubscription
            ? 'one_week'
            : 'no_subscription';

      // TODO: maybe add "is_paid_user" ?
      posthog.capture('$set', {
        $set: {
          gynger_active_subscription: activeSubscription,
        },
      });
    },
  },
});

/**
 * `actions` will be used to trigger change in the state from where ever you want
 */
export const { actions: userActions, reducer: userReducer } = slice;
